import { FC, SVGProps } from 'react'
import { useParams } from 'react-router-dom'
import { useGetProjectsQuery } from 'src/reactQuery/useGetProjectsQuery'
import { ProjectDetails } from 'src/pages/ProjectDetails/index'
import { SmartInsightsAIRecommendations } from 'src/charts/SmartInsightsAIRecommendations'

import { ProjectsWidget } from 'src/components/ProjectsWidget'
import { formatIndustries } from 'src/components/ProjectsWidget/projectAppearance'

import styles from './projectsPage.module.scss'

const DashboardIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <rect height="6" rx="1.5" stroke="currentColor" width="6" x="4" y="4" />
    <rect height="6" rx="1.5" stroke="currentColor" width="6" x="14" y="4" />
    <rect height="6" rx="1.5" stroke="currentColor" width="6" x="4" y="14" />
    <rect height="6" rx="1.5" stroke="currentColor" width="6" x="14" y="14" />
  </svg>
)

const FolderIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <path
      d="M3.5 7.5H9l1.7 2H20.5v9H3.5v-11Z"
      stroke="currentColor"
      strokeLinejoin="round"
    />
  </svg>
)

const BoltIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <path
      d="M13.5 3.5 6.5 13h5l-1 7.5 7-10h-5l1-7Z"
      stroke="currentColor"
      strokeLinejoin="round"
    />
  </svg>
)

const BuildingIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <path d="M7 20V4h10v16" stroke="currentColor" strokeLinejoin="round" />
    <path d="M5 20h14" stroke="currentColor" strokeLinecap="round" />
    <path
      d="M10 8h.01M14 8h.01M10 12h.01M14 12h.01M10 16h.01M14 16h.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2"
    />
  </svg>
)

const UpdateIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <path
      d="M17.8 8.4A6.5 6.5 0 0 0 6 10.6M6.2 15.6A6.5 6.5 0 0 0 18 13.4"
      stroke="currentColor"
      strokeLinecap="round"
    />
    <path d="M18 4.8v3.8h-3.8M6 19.2v-3.8h3.8" stroke="currentColor" />
  </svg>
)

const isToday = (value: string) => {
  const date = new Date(value)
  const today = new Date()

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  )
}

export const ProjectsPage: FC = () => {
  const { projectId } = useParams<{
    projectId: string
  }>()
  const { data } = useGetProjectsQuery()
  const projects = data?.data || []

  if (projectId) {
    return (
      <div className={styles.projectsPage}>
        <div className={styles.projectDetailsOutlet}>
          <ProjectDetails />
        </div>
      </div>
    )
  }

  const uniqueIndustries = Array.from(
    new Set(projects.flatMap((project) => project.industries || []))
  )

  const lastUpdatedProject = [...projects].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  )[0]

  const summaryCards = [
    {
      Icon: FolderIcon,
      label: 'Total Projects',
      value: String(projects.length),
    },
    {
      Icon: BoltIcon,
      label: 'Campaigns Running',
      value: String(
        projects.reduce(
          (sum, project) => sum + (project.linkedItemsCount || 0),
          0
        )
      ),
    },
    {
      Icon: BuildingIcon,
      label: 'Industry',
      metaItems: uniqueIndustries
        .slice(0, 3)
        .map((industry) => formatIndustries([industry])),
      value: String(uniqueIndustries.length),
    },
    {
      Icon: UpdateIcon,
      label: 'Last Update',
      metaItems: lastUpdatedProject
        ? [
            new Date(lastUpdatedProject.updatedAt).toLocaleString('en-US', {
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              month: 'short',
            }),
          ]
        : ['No updates yet'],
      value: lastUpdatedProject
        ? isToday(lastUpdatedProject.updatedAt)
          ? 'Today'
          : new Date(lastUpdatedProject.updatedAt).toLocaleDateString('en-US', {
              day: 'numeric',
              month: 'short',
            })
        : '-',
    },
  ]

  return (
    <div className={styles.projectsPage}>
      <section className={styles.overallSummary}>
        <div className={styles.summaryHeader}>
          <DashboardIcon className={styles.summaryHeaderIcon} />
          <h1 className={styles.summaryTitle}>Overall Summary</h1>
        </div>

        <div className={styles.summaryCardsGrid}>
          {summaryCards.map(({ Icon, label, metaItems, value }) => (
            <article className={styles.summaryCard} key={label}>
              <div className={styles.summaryCardHeader}>
                <Icon className={styles.summaryCardIcon} />
                <span className={styles.summaryCardLabel}>{label}</span>
              </div>

              <div className={styles.summaryCardBody}>
                <span className={styles.summaryCardValue}>{value}</span>
                {metaItems && (
                  <div className={styles.summaryCardMeta}>
                    {metaItems.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>

        <ProjectsWidget variant="page" />
      </section>

      <SmartInsightsAIRecommendations
        className={styles.smartInsightsSpacing}
        homePageVariant
        showScopeTabs
      />
    </div>
  )
}
